import { Button, CloseButton, Text, TextInput, Title } from "@mantine/core";
import { closeModal, openContextModal } from "@mantine/modals";
import type { DesktopProfile } from "@src/electron-api";
import type React from "react";
import { useEffect, useState } from "react";
import { useDesktopProfiles } from "../../hooks/use-desktop-profiles";

let modalId: string | null = null;

export function closeCreateProfileModal() {
	if (modalId) {
		closeModal(modalId);
		modalId = null;
	}
}

function CreateProfileModalContent({
	onClose,
	onCreated,
}: {
	onClose: () => void;
	onCreated?: (profile: DesktopProfile) => void;
}) {
	const { createProfile } = useDesktopProfiles();
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (e?: React.FormEvent) => {
		e?.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) {
			setError("Profile name is required");
			return;
		}

		try {
			setSubmitting(true);
			const created = await createProfile(trimmed);
			if (created) onCreated?.(created);
			onClose();
		} catch (err) {
			console.error("Failed to create profile:", err);
			setError("Failed to create profile. Please try again.");
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<div>
					<Title order={4} className="text-text-primary font-bold">
						Create Profile
					</Title>
					<Text size="xs" className="text-text-secondary mt-0.5">
						Create a new desktop profile with isolated session and cookies.
					</Text>
				</div>
				<CloseButton onClick={onClose} disabled={submitting} />
			</div>

			<TextInput
				label="Profile Name"
				placeholder="e.g. Work, Personal, Client A"
				value={name}
				onChange={(e) => {
					setName(e.currentTarget.value);
					if (error) setError(null);
				}}
				error={error}
				autoFocus
				required
			/>

			<div className="flex items-center justify-end gap-3 pt-1">
				<Button
					variant="subtle"
					color="gray"
					onClick={onClose}
					disabled={submitting}
				>
					Cancel
				</Button>
				<Button
					variant="cta"
					type="submit"
					loading={submitting}
					disabled={!name.trim()}
				>
					Create Profile
				</Button>
			</div>
		</form>
	);
}

export function openCreateProfileModal(
	onCreated?: (profile: DesktopProfile) => void,
) {
	closeCreateProfileModal();
	modalId = openContextModal({
		modal: "buttonsModal",
		centered: true,
		withCloseButton: false,
		padding: 0,
		radius: "md",
		size: "420px",
		zIndex: 9999,
		onClose: () => {
			modalId = null;
		},
		innerProps: {
			children: (
				<CreateProfileModalContent
					onClose={closeCreateProfileModal}
					onCreated={onCreated}
				/>
			),
		},
	});
}

/**
 * Global listener to allow Electron native menu to trigger the create profile modal
 */
export function useCreateProfileMenuListener() {
	useEffect(() => {
		const handleMenuTrigger = () => openCreateProfileModal();
		window.addEventListener("illumi-create-profile-modal", handleMenuTrigger);
		return () => {
			window.removeEventListener(
				"illumi-create-profile-modal",
				handleMenuTrigger,
			);
		};
	}, []);
}
